import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Card, CardHeader } from "src/components/commons/Card";
import { MATCH_ACTION, STATUS_MAP } from "src/helpers/constants";
import Button from "@material-ui/core/Button";
import { Link } from "react-router-dom";
import { SchedulesList } from "../commons/SchedulesList";
import { Chip } from "../commons/Chip";
import { SuccessfulMatches } from "./SuccessfulMatches";
import Table from "src/components/commons/Table/Table";
import StatusChip from '../commons/StatusChip';
import { getFounderMatches, getVendorWithUserAnswersForAdmin, updateFounderVendorMatchStatus } from 'src/api/vendorServices';
import { populateCompetitors } from "src/api/companyService";
import { formatPhone } from 'src/helpers';
import { Box } from "@mui/material";
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DashboardDetails from './DashboardDetails';
import { getMatchActionButtons } from 'src/helpers/matchActions.jsx';
import { renderSafeValue } from 'src/helpers';
import { grantRecommendations } from "src/api/grantsRecommendation";
import { getInitials } from 'src/helpers/grantsRecommendation';
import { getMyOutreach } from 'src/api/outreach';
// import { PendingMatches } from "./PendingMatches";

export function FounderDashboard({ allCampaigns = [] }) {
	const { authUser } = useSelector(({ auth }) => auth);
	const [matches, setMatches] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [viewOpen, setViewOpen] = useState(false);
	const [viewDetails, setViewDetails] = useState(null);
	const [companies, setCompanies] = useState([]);
	const [grants, setGrants] = useState([]);
	const [outreach, setOutreach] = useState([]);

	useEffect(() => {
		async function fetchMatches() {
			try {
				setLoading(true);
				setError(false);
				const matchesArr = await getFounderMatches(authUser.id, { page: page + 1, limit: rowsPerPage });
				setMatches(matchesArr || []);
				setTotalCount((matchesArr && matchesArr.length > 0 && matchesArr[0].total_count) || 0);
			} catch (e) {
				setError(true);
			} finally {
				setLoading(false);
			}
		}
		if (authUser?.id) fetchMatches();
	}, [authUser?.id, page, rowsPerPage]);

	useEffect(() => {
		fetchCompanies();
		fetchGrantRecommendations();
		fetchOutreach();
	  }, []);
	
	  const fetchCompanies = async () => {
		setLoading(true);
		setError(null);
	
		try {
		  const response = await populateCompetitors();
	
			if (!response) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			setCompanies(response.list);
		} catch (err) {
		  setError(err instanceof Error ? err.message : 'An error occurred');
		  console.error('Error fetching companies:', err);
		} finally {
		  setLoading(false);
		}
	  };

	  const fetchGrantRecommendations = async() => {
		try {
			setLoading(true);
			setError(null);
			const response = await grantRecommendations();
			// const data = await response.json();
			console.log(response)

			// Map grants to UI-friendly format
			const mappedGrants = response.map((grant) => ({
			id: grant.id,
			title: grant.title,
			agency: grant.agency,
			agencyCode: grant.agencyCode,
			number: grant.number,
			openDate: grant.openDate,
			closeDate: grant.closeDate,
			cfdaList: grant.cfdaList || [],
			detailUrl: grant.detailUrl,
			oppStatus: grant.oppStatus,
			// For UI consistency with companies
			logo_url: null, // no logo
			company_name: grant.title.length > 50 ? grant.title.slice(0, 47) + "..." : grant.title,
			industry: `${grant.agencyCode} • ${grant.cfdaList.slice(0, 2).join(", ")}`,
			city_state_country: null,
			estimated_number_employees: null,
			annual_revenue: null,
			keywords: grant.cfdaList.slice(0, 3), // use CFDA codes as "keywords"
			url: grant.detailUrl,
			linkedin_url: null,
			twitter_url: null,
			}));

        	setGrants(mappedGrants);
		  } catch (err) {
			console.error("Error fetching recommendations:", err);
		  } finally {
			setLoading(false);
		  }
	  }

	  const fetchOutreach = async() => {
		try {
			setLoading(true);
			setError(null);
			const response = await getMyOutreach();
			console.log('Outreach response:', response);
			// Check if response has a data property, otherwise use response directly
			const outreachData = response?.data || response || [];
			console.log('Setting outreach data:', outreachData);
			setOutreach(outreachData);
		} catch (err) {
			console.error("Error fetching outreach:", err);
		} finally {
			setLoading(false);
		}
	  }

	  const formatNumber = (num) => {
		if (!num) return 'N/A';
		return new Intl.NumberFormat('en-US').format(num);
	  };
	
	  const getCompanyInitials = (name) => {
		return name
		  .split(' ')
		  .map(word => word[0])
		  .join('')
		  .toUpperCase()
		  .slice(0, 2);
	  };

	const handleViewVendor = async (row) => {
		const vendorServiceId = row.vendor_service_id || row.vendor_service || row.id;
		console.log(vendorServiceId);
		console.log(row);
		try {
			const details = await getVendorWithUserAnswersForAdmin(vendorServiceId);
			console.log(details);
			setViewDetails(details);
			setViewOpen(true);
		} catch (e) {
			setViewDetails(null);
			setViewOpen(false);
		}
	};

	const handleAccept = async (row) => {
		try {
			const updatedMatch = await updateFounderVendorMatchStatus(row.id, { status: 'active' });
			// Update the local state immediately
			setMatches(prevMatches => 
				prevMatches.map(match => 
					match.id === row.id 
						? { ...match, status: 'active' }
						: match
				)
			);
		} catch (e) {
			console.error('Error accepting match:', e);
		}
	};

	const handleDecline = async (row) => {
		try {
			const updatedMatch = await updateFounderVendorMatchStatus(row.id, { 
				status: 'declined',
				call_status: 'declined'
			});
			// Update the local state immediately
			setMatches(prevMatches => 
				prevMatches.map(match => 
					match.id === row.id 
						? { ...match, status: 'declined', call_status: 'declined' }
						: match
				)
			);
		} catch (e) {
			console.error('Error declining match:', e);
		}
	};

	return (
		<div>
			<div className="grid grid-cols-1 gap-4">
				<div className="grid grid-cols-1 md:grid-cols-3 gap-4 min-h-[250px]">
					<Card>
						<CardHeader>
							<span className="text-lg">Round</span>
						</CardHeader>
						<div className="text-slate-400 flex justify-center items-center h-full">No Data Found</div>
					</Card>
					<Card>
						<CardHeader>
							<span className="text-lg">Valuation</span>
						</CardHeader>
						<div className="text-slate-400 flex justify-center items-center h-full">No Data Found</div>
					</Card>
					<Card>
						<CardHeader>
							<span className="text-lg">Schedules</span>
						</CardHeader>
						<div className="text-slate-400 flex justify-center items-center h-full">No Data Found</div>
						{/* <SchedulesList acceptedMatches={matches} /> */}
					</Card>
				</div>

				{!loading && companies.length > 0 && (
				<div className="space-y-4">
					{/* General Header for the entire section */}
					<h2 className="text-xl font-bold p-4 rounded-md bg-white">
						Competitors In Your Space
					</h2>
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
					{companies.map((company) => (
						<Card key={company.id} className="hover:shadow-lg transition-shadow">
						<CardHeader className="pb-3">
							<div className="flex items-start gap-3">
							{company.logo_url ? (
								<img
								src={company.logo_url}
								alt={company.company_name}
								className="w-12 h-12 rounded object-cover"
								onError={(e) => {
									e.currentTarget.style.display = 'none';
									e.currentTarget.nextElementSibling?.classList.remove('hidden');
								}}
								/>
							) : null}
							<div
								className={`w-12 h-12 rounded bg-primary/10 flex items-center justify-center text-primary font-semibold ${
								company.logo_url ? 'hidden' : ''
								}`}
							>
								{getCompanyInitials(company.company_name)}
							</div>
							<div className="flex-1 min-w-0">
								<h3 className="font-semibold text-sm truncate" title={company.company_name}>
								{company.company_name}
								</h3>
								{company.industry && (
								<p className="text-xs text-slate-500 truncate">{company.industry}</p>
								)}
							</div>
							</div>
						</CardHeader>
						
						<div className="px-4 pb-4 space-y-2">
							{company.city_state_country && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
								<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
								</svg>
								<span className="truncate">{company.city_state_country}</span>
							</div>
							)}
							
							{company.estimated_number_employees && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
								<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
								</svg>
								<span>{formatNumber(company.estimated_number_employees)} employees</span>
							</div>
							)}
							
							{company.annual_revenue && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
								<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
								<span>${formatNumber(company.annual_revenue)}</span>
							</div>
							)}

							{company.keywords && company.keywords.length > 0 && (
							<div className="flex flex-wrap gap-1 mt-2">
								{company.keywords.slice(0, 3).map((keyword, index) => (
								<span
									key={index}
									className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
								>
									{keyword}
								</span>
								))}
								{company.keywords.length > 3 && (
								<span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
									+{company.keywords.length - 3}
								</span>
								)}
							</div>
							)}

							<div className="flex gap-2 mt-3 pt-3 border-t">
							{company.linkedin_url && (
								<a
								href={`https://${company.linkedin_url}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
								>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
									<path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
								</svg>
								</a>
							)}
							{company.twitter_url && (
								<a
								href={`https://${company.twitter_url}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
								>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
									<path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
								</svg>
								</a>
							)}
							{company.url && (
								<a
								href={`https://${company.url}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
								>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
								</svg>
								</a>
							)}
							</div>
						</div>
						</Card>
					))}
				</div>
				</div>
				)}

				{!loading && outreach.length > 0 && (
				<div className="space-y-4">
				{/* Header */}
				<h2 className="text-xl font-bold p-4 rounded-md bg-white">
					Funder Outreach
				</h2>

				{/* Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
					{outreach.map((item) => (
					<Card key={item.id} className="hover:shadow-lg transition-shadow">
						<CardHeader className="pb-3">
						<div className="flex items-start gap-3">
							<div className="w-12 h-12 rounded bg-green-100 flex items-center justify-center text-green-600 font-semibold">
							{getCompanyInitials(item.funder_name || 'Funder')}
							</div>

							<div className="flex-1 min-w-0">
							<h3 className="font-semibold text-sm truncate" title={item.funder_name}>
								{item.funder_name || 'Unknown Funder'}
							</h3>
							</div>
						</div>
						</CardHeader>

						<div className="px-4 pb-4 space-y-2">
						{/* Email */}
						{item.funder_email && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
							<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
							</svg>
							<span className="truncate">{item.funder_email}</span>
							</div>
						)}

						{/* Strategy/Investment */}
						{item.funder_strategy && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
							<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2zm0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v10m-6 0a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2m0 0V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2z" />
							</svg>
							<span className="truncate">{item.funder_strategy}</span>
							</div>
						)}

						{/* Social Links */}
						<div className="flex gap-2 mt-3 pt-3 border-t">
							{item.funder_linkedin && (
							<a
								href={item.funder_linkedin.startsWith('http') ? item.funder_linkedin : `https://${item.funder_linkedin}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
							>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
								<path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
								</svg>
							</a>
							)}
							{item.funder_twitter && (
							<a
								href={item.funder_twitter.startsWith('http') ? item.funder_twitter : `https://twitter.com/${item.funder_twitter.replace('@', '')}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
							>
								<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
								<path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
								</svg>
							</a>
							)}
							{item.funder_website && (
							<a
								href={item.funder_website.startsWith('http') ? item.funder_website : `https://${item.funder_website}`}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
								</svg>
							</a>
							)}
						</div>
						</div>
					</Card>
					))}
				</div>
				</div>
				)}

				{!loading && grants.length > 0 && (
				<div className="space-y-4">
				{/* Header */}
				<h2 className="text-xl font-bold p-4 rounded-md bg-white">
					Grant Opportunities
				</h2>

				{/* Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
					{grants.map((grant) => (
					<Card key={grant.id} className="hover:shadow-lg transition-shadow">
						<CardHeader className="pb-3">
						<div className="flex items-start gap-3">
							{/* Placeholder logo (initials) */}
							<div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center text-primary font-semibold">
							{getInitials(grant.agency)}
							</div>

							<div className="flex-1 min-w-0">
							<h3
								className="font-semibold text-sm truncate"
								title={grant.title}
							>
								{grant.company_name}
							</h3>
							{grant.industry && (
								<p className="text-xs text-slate-500 truncate">
								{grant.industry}
								</p>
							)}
							</div>
						</div>
						</CardHeader>

						<div className="px-4 pb-4 space-y-2">
						{/* Grant Number */}
						{grant.number && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
							<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
							</svg>
							<span className="truncate">{grant.number}</span>
							</div>
						)}

						{/* Dates */}
						{grant.closeDate && (
							<div className="flex items-center gap-1 text-xs text-slate-600">
							<svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
							</svg>
							<span>Closes {grant.closeDate}</span>
							</div>
						)}

						{/* CFDA "Keywords" */}
						{grant.keywords && grant.keywords.length > 0 && (
							<div className="flex flex-wrap gap-1 mt-2">
							{grant.keywords.map((cfda, index) => (
								<span
								key={index}
								className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded"
								>
								{cfda}
								</span>
							))}
							</div>
						)}

						{/* Action Link */}
						<div className="flex gap-2 mt-3 pt-3 border-t">
							{grant.url && (
							<a
								href={grant.url}
								target="_blank"
								rel="noopener noreferrer"
								className="text-slate-400 hover:text-primary transition-colors"
							>
								<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
								</svg>
							</a>
							)}
						</div>
						</div>
					</Card>
					))}
				</div>
				</div>
				)}

				<Card>
					<div className="p-4 rounded-t-md border border-b-gray-200 flex justify-between items-center">
						<h2 className="text-lg">Matches</h2>
						<span className="text-primary cursor-pointer">
						<a href="/founder-matches">See all</a>
					</span>
					</div>
					<Table
						columns={[
							{ header: 'Offered Service', accessor: 'offered_service', align: 'left', render: (_, row) => renderSafeValue(row.offered_service, 'offered_service') },
							{ 
								header: 'Match Status', 
								accessor: 'status', 
								align: 'center', 
								render: (_, row) => <StatusChip status={row.status} />
							},
							{ 
								header: 'Call Status', 
								accessor: 'call_status', 
								align: 'center', 
								render: (_, row) => <StatusChip status={row.call_status} />
							},
							{ header: 'Vendor Name', accessor: 'vendor_name', align: 'center' },
							{ header: 'Vendor Email', accessor: 'vendor_email', align: 'center' },
							{ header: 'Action', accessor: 'action', align: 'center', render: (_, row) => 
								getMatchActionButtons({
									row,
									onView: handleViewVendor,
									onAccept: handleAccept,
									onDecline: handleDecline
								})
							},
						]}
						data={matches}
						loading={loading}
						error={error}
						page={page}
						rowsPerPage={rowsPerPage}
						totalCount={totalCount}
						onPageChange={(_, newPage) => setPage(newPage)}
						onRowsPerPageChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
						stickyHeader
						className="border-0"
						emptyStateMessage="There are no pending matches at the moment"
					/>
				</Card>
				{/* <SuccessfulMatches acceptedMatches={matches} currentRole={authUser.role} /> */}
				{/* <PendingMatches pendingMatches={pendingMatches} currentRole={currentRole} /> */}
				<hr className="my-4" />
				<Card>
					<CardHeader>
						<span className="text-xl">All Campaigns</span>
					</CardHeader>
					<Table
						columns={[
							{ header: 'Name', accessor: 'name', align: 'left' },
							{ header: 'Status', accessor: 'status', align: 'center', render: (value) => <StatusChip status={value} /> },
							{ header: 'Actions', accessor: 'actions', align: 'right', render: (_, row) => (
								<Button
									variant="contained"
									style={{ backgroundColor: '#3096F5', color: '#fff' }}
									href={`/campaign/${row.id}/details`}
								>
									View
								</Button>
							) },
						]}
						data={allCampaigns.data}
						loading={false}
						error={false}
						page={0}
						rowsPerPage={allCampaigns.data.length}
						totalCount={allCampaigns.data.length}
						onPageChange={() => {}}
						onRowsPerPageChange={() => {}}
						stickyHeader
						className="border-0"
						emptyStateMessage="No campaigns found."
					/>
				</Card>
			</div>
			{/* Vendor Details Modal */}
			<Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>Vendor Details</DialogTitle>
				<DialogContent>
					{viewDetails ? (
						<DashboardDetails 
							details={viewDetails} 
							userType="FOUNDER" 
						/>
					) : (
						<div>No details found.</div>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}
